import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Badge,
  EmptyState,
  Page,
  Switch,
} from "../../../components/ui";
import { DetailHeader, FactList } from "../../../components/detail";
import { getUser, updateUserDetails } from "../../../services/users";
import { getUserInfo } from "../../../store/actions/uploadMediaAction";
import { getDate } from "../../../utils/format";
import { capitalize } from "../../../utils/list";
import "../../../components/detail/detail.css";
import { AccountStatusBadge, ActivityBadge } from "./accountBadges";
import UserActivity from "./UserActivity";
import UserSubscription from "./UserSubscription";
import UserOverview from "./UserOverview";
import WinbackComposer from "./WinbackComposer";
import { can } from "../../../utils/roles";
import "./user-list.css";

const fullName = (user) =>
  [user?.name, user?.last_name].filter(Boolean).join(" ") || "N/A";

/** Details of the user picked in the users list (kept in the redux store). */
const UserInfo = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userDetailsData } = useSelector((state) => state.uploadMediaReducer);
  const user = (userDetailsData ?? [])[0];

  // The switch updates right away, then follows the store once the user is reloaded.
  const [active, setActive] = useState(Boolean(user?.status));
  // Relance ouverte : "generic" (« Vous nous avez manqué ») ou "donation" (appel au don)
  const [winback, setWinback] = useState(null);
  const canEmail = can("users", "edit");
  useEffect(() => {
    setActive(Boolean(user?.status));
  }, [user?.status]);

  const reloadUser = () =>
    getUser(user?._id).then((data) => dispatch(getUserInfo(data)));

  const update = (changes) =>
    updateUserDetails({ _id: user?._id, ...changes }).then(reloadUser);

  const changeStatus = (checked) => {
    setActive(checked);
    update({ status: checked });
  };

  if (!user) {
    return (
      <Page>
        <DetailHeader title={t("user info")} />
        <EmptyState icon="ri-user-search-line">{t("No Data Found")}</EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      <DetailHeader
        title={fullName(user)}
        badges={
          <>
            <AccountStatusBadge status={user.status} />
            <ActivityBadge lastActiveAt={user.lastActiveAt} withDate={false} />
            {user.subscription === "free" ? (
              <Badge color="slate">{t("Free")}</Badge>
            ) : (
              <Badge color="amber">{t("Paid")}</Badge>
            )}
          </>
        }
        actions={
          canEmail && (
            <>
              <button
                type="button"
                className="ui-btn"
                disabled={!user.email || user.marketingOptOut}
                title={
                  !user.email
                    ? t("No email address")
                    : user.marketingOptOut
                    ? t("Unsubscribed from information emails")
                    : undefined
                }
                onClick={() => setWinback("generic")}
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                {t("Send « We've missed you »")}
              </button>
              {/* Appel au don individuel : même fenêtre, message « Appel au don » */}
              <button
                type="button"
                className="ui-btn ui-btn-secondary"
                disabled={!user.email || user.marketingOptOut}
                title={
                  !user.email
                    ? t("No email address")
                    : user.marketingOptOut
                    ? t("Unsubscribed from information emails")
                    : undefined
                }
                onClick={() => setWinback("donation")}
              >
                <i className="ri-heart-line" aria-hidden="true"></i>
                {t("Send a call for donations")}
              </button>
            </>
          )
        }
      />

      <div className="detail-layout">
        <section className="ui-card detail-pad">
          <div className="user-profile">
            {user.profile_image?.url ? (
              <img src={user.profile_image.url} alt="" />
            ) : (
              <span className="users-avatar" aria-hidden="true">
                {(user.name || "?").charAt(0).toUpperCase()}
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <strong>{fullName(user)}</strong>
              <span>{user.email || "N/A"}</span>
            </div>
          </div>
          <FactList
            plain
            items={[
              { label: t("email"), value: user.email },
              {
                label: t("phone number"),
                value: user.phone
                  ? [user.dial_code, user.phone].filter(Boolean).join(" ")
                  : undefined,
              },
              {
                label: t("date of birth"),
                value: user.dob ? user.dob.substring(0, 10) : undefined,
              },
              { label: t("gender"), value: capitalize(user.gender) },
              { label: t("language"), value: capitalize(user.language) },
              { label: t("country"), value: user.country },
              {
                label: t("Registered"),
                value: user.createdAt ? getDate(user.createdAt) : undefined,
              },
              {
                label: t("Last « We've missed you » email"),
                value: user.winbackEmailAt ? getDate(user.winbackEmailAt) : t("Never"),
              },
              ...(user.marketingOptOut
                ? [{ label: t("Information emails"), value: t("Unsubscribed") }]
                : []),
            ]}
          />
          <UserActivity userId={user._id} lastActiveAt={user.lastActiveAt} />
        </section>

        <section className="ui-card detail-pad">
          <div className="user-setting">
            <div>
              <h3 className="detail-section-title">{t("Account status")}</h3>
              <p>
                {t(
                  "Enable or disable the account. This is an admin setting, independent from the user's app activity."
                )}
              </p>
            </div>
            <Switch
              checked={active}
              onChange={changeStatus}
              label={t("Account status")}
              onLabel={t("Enabled")}
              offLabel={t("Disabled")}
            />
          </div>
        </section>
      </div>

      <section className="ui-card detail-pad user-subscription-card">
        <UserSubscription userId={user._id} onChange={reloadUser} />
      </section>

      <UserOverview userId={user._id} />

      {winback && (
        <WinbackComposer
          user={user}
          initialMode={winback}
          onClose={() => setWinback(null)}
          onSent={() => {
            setWinback(null);
            reloadUser();
          }}
        />
      )}
    </Page>
  );
};

export default UserInfo;
